import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  fetchTsUploadAccess,
  grantCrmTsUploadBypass,
} from "../../store/admin/tsUploadAccessSlice";

const formatRemaining = (expiresAt: string | null) => {
  if (!expiresAt) return "";
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

export const TsUploadAccessBar = ({ caseId }: { caseId?: string | null }) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { access, loading, granting, error, caseId: loadedCaseId } =
    useAppSelector((state) => state.tsUploadAccess);
  const [remaining, setRemaining] = useState("");

  const isSuperAdmin = user?.role === "Super Admin";
  const crmBlocked = Boolean(access && !access.allowed && !isSuperAdmin);

  useEffect(() => {
    if (!caseId) return;
    dispatch(fetchTsUploadAccess(caseId));
  }, [caseId, dispatch]);

  useEffect(() => {
    if (!access?.bypassExpiresAt) {
      setRemaining("");
      return;
    }
    const tick = () => {
      const label = formatRemaining(access.bypassExpiresAt);
      setRemaining(label);
      if (label === "expired" && caseId) {
        dispatch(fetchTsUploadAccess(caseId));
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [access?.bypassExpiresAt, caseId, dispatch]);

  if (!caseId) return null;

  return (
    <div className="flex flex-col gap-1 min-w-0">
      {loading && loadedCaseId !== caseId ? (
        <p className="text-[11px] text-[#AAAAAA]">Checking upload access…</p>
      ) : crmBlocked ? (
        <p className="text-[11px] text-amber-800 bg-amber-50 ring-1 ring-amber-200 rounded-lg px-2 py-1">
          {access?.message ||
            "CRM can upload after Ready to Upload or once the case is Completed."}
          {access?.allocationStatus
            ? ` Current allocation: ${access.allocationStatus}.`
            : " Allocation steps are not complete."}
        </p>
      ) : access?.reason === "on_hold" ? (
        <p className="text-[11px] text-sky-800 bg-sky-50 ring-1 ring-sky-200 rounded-lg px-2 py-1">
          Case is on hold (query pending). Upload is allowed; status will not
          auto-complete until hold is closed.
        </p>
      ) : access?.bypassExpiresAt && remaining && remaining !== "expired" ? (
        <p className="text-[11px] text-emerald-800 bg-emerald-50 ring-1 ring-emerald-200 rounded-lg px-2 py-1">
          CRM bypass active · {remaining} left
          {access.bypassGrantedBy ? ` · granted by ${access.bypassGrantedBy}` : ""}
        </p>
      ) : null}
      {error ? (
        <p className="text-[11px] text-red-600">{error}</p>
      ) : null}
      {isSuperAdmin && access && !access.atReadyToUpload ? (
        <button
          type="button"
          onClick={() => dispatch(grantCrmTsUploadBypass(caseId))}
          disabled={granting}
          className="inline-flex items-center justify-center rounded-xl border border-[#2fa9bf] bg-white px-3 py-2 text-xs font-medium text-[#2fa9bf] hover:bg-[#2fa9bf]/10 disabled:opacity-60"
        >
          {granting ? "Granting…" : "Allow Operations upload (15 min)"}
        </button>
      ) : null}
    </div>
  );
};

export default TsUploadAccessBar;
